/**
 * The browser fallback for notifications (R-HL-2), mounted once in the app shell. Its nudge
 * is a notice in the top bar's notices item (fallbackNotice), not a banner. With no enabled channel it raises new approvals, reminders and blocked
 * missions as OS notifications (tab hidden or unfocused) or toasts (tab in front), keeps the
 * count in the tab title, and offers to turn alerts on from a click: web push when the hub has
 * a notify module (reaches the browser with Orgus closed), else this browser's permission.
 * The rules are in fallback.ts.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { BellOff, BellRing, Smartphone } from '@orgus/ui/icons';
import { approvalOutcomeOf } from '@orgus/protocol';
import type { ApprovalsState } from '@orgus/protocol';
import { Button, ErrorState, formatClock, toUiError, toast } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { enablePush, pushEnv, pushReadiness, usePwa } from '../mobile/pwa.ts';
import { useApprovals } from '../approvals/live.ts';
import { useNotifyState } from './api.ts';
import { DISMISS_KEY, SHOWN_KEY, bannerState, candidateAlerts, deliveryOf, fallbackActive, fallbackStep, readStore, remember, titleWithCount } from './fallback.ts';
import type { BannerState, FallbackAlert } from './fallback.ts';
import { useNotice } from '../../components/shell/notices.ts';
import type { Notice } from '../../components/shell/notices.ts';
import { approvalOfUrl, openAnswer } from '../approvals/AnswerSheet.tsx';

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): void {
  try {
    storage()?.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full or blocked: dedupe falls back to the OS tag */
  }
}

/** Decided requests that still block a session (expired or denied, not handled). */
export function blockedCount(s: Partial<ApprovalsState> | null | undefined): number {
  return (s?.blocking ?? []).filter((a) => approvalOutcomeOf(a) && !a.dismissedAt && !a.laterWorkAt).length;
}

/** Waiting plus blocked requests, for the tab title and the rail's Inbox badge. */
export function attentionCount(s: Partial<ApprovalsState> | null | undefined): number {
  return (s?.pending?.length ?? 0) + blockedCount(s);
}

/** Whether "Turn on alerts" can subscribe this browser to web push from this hub. */
export function canPushHere(vapidPublicKey: string | null | undefined, mock: boolean): boolean {
  return !!vapidPublicKey && !mock && pushReadiness(pushEnv()) === 'ready';
}

/**
 * "Turn on alerts" (R-HL-2): web push when the hub can send it, so requests reach this browser
 * with every Orgus tab closed (the browser becomes a push channel); else this browser's
 * notification permission for alerts while a console tab is open. Throws what failed and why.
 */
export async function turnOnAlerts(vapidPublicKey: string | null | undefined, mock: boolean): Promise<'push' | 'tab' | 'blocked'> {
  if (canPushHere(vapidPublicKey, mock)) {
    await enablePush(vapidPublicKey!);
    return 'push';
  }
  if (typeof Notification === 'undefined') return 'blocked';
  const p = await Notification.requestPermission();
  usePwa.setState({ permission: p });
  return p === 'granted' ? 'tab' : 'blocked';
}

/** The toast after turning alerts on: says how far they reach. */
export function alertsOnToast(how: 'push' | 'tab' | 'blocked'): void {
  if (how === 'push') toast('Alerts are on, even with Orgus closed.', { tone: 'success' });
  else if (how === 'tab') toast('Alerts are on in this browser.', { tone: 'success', description: 'Set up phone push to get them with Orgus closed.' });
}

/** The banner, without store reads: rendered in tests. `push`: "Turn on alerts" subscribes to web push. */
export function FallbackBanner({ state, onAllow, onDismiss, push = false, error }: { state: BannerState; onAllow: () => void; onDismiss: () => void; push?: boolean; error?: unknown }) {
  if (state === 'none') return null;
  const denied = state === 'denied';
  return (
    <div role="status" className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-waiting/40 bg-waiting-soft px-4 py-2 text-ui text-fg">
      {/* Below 768 px the message takes the whole line and the actions wrap under it. */}
      <span className="flex min-w-0 flex-1 basis-full items-start gap-3 md:basis-0">
        <span className="flex h-5 shrink-0 items-center">
          {denied ? <BellOff className="size-4 text-waiting" aria-hidden /> : <BellRing className="size-4 text-waiting" aria-hidden />}
        </span>
        <p className="min-w-0 flex-1">
          {denied ? (
            <>
              <span className="font-medium">Alerts are blocked in this browser.</span> Allow notifications for this site in the browser's site settings, or set up phone push.
            </>
          ) : (
            <>
              <span className="font-medium">Approvals can wait unseen.</span> Nothing is set up to reach you outside this console.{' '}
              {push ? 'Turn on alerts so requests reach you even when Orgus is closed.' : 'Turn on alerts so a request reaches you while this tab is in the background, or set up phone push to get them with Orgus closed.'}
            </>
          )}
        </p>
      </span>
      <span className="flex shrink-0 flex-wrap items-center gap-2">
        {!denied && (
          <Button size="sm" variant="secondary" icon={<BellRing />} onClick={onAllow}>
            Turn on alerts
          </Button>
        )}
        <Link to="/settings/notifications" className="inline-flex h-control-sm items-center gap-2 rounded-control px-2 font-medium text-fg-muted hover:bg-hover hover:text-fg [&_svg]:size-4">
          <Smartphone aria-hidden />
          Set up phone push
        </Link>
        <Button size="sm" variant="ghost" onClick={onDismiss}>
          Not now
        </Button>
      </span>
      {error ? <ErrorState variant="inline" error={error} className="basis-full" /> : null}
    </div>
  );
}

/**
 * The same message as FallbackBanner, as a notice in the top bar's notices item (live brief,
 * section 3: notices collapse into one status item instead of stacking banners on every page).
 */
export function fallbackNotice({ state, onAllow, onDismiss, push = false, error }: { state: BannerState; onAllow: () => void | Promise<void>; onDismiss: () => void; push?: boolean; error?: unknown }): Notice | null {
  if (state === 'none') return null;
  const denied = state === 'denied';
  const phone = { id: 'phone', label: 'Set up phone push', to: '/settings/notifications' } as const;
  const later = { id: 'dismiss', label: 'Not now', run: onDismiss } as const;
  return denied
    ? {
        id: 'notify.fallback',
        tone: 'attention',
        kind: 'alerts-off',
        order: 10,
        title: 'Alerts are blocked in this browser',
        body: "Allow notifications for this site in the browser's site settings.",
        actions: [{ ...phone, emphasis: 'secondary' }, later],
        error,
      }
    : {
        id: 'notify.fallback',
        tone: 'attention',
        kind: 'alerts',
        order: 10,
        title: 'Approvals can wait unseen',
        body: push ? 'Turn on alerts so requests reach you even when Orgus is closed.' : 'Turn on alerts so requests reach you while this tab is in the background.',
        actions: [{ id: 'allow', label: 'Turn on alerts', run: onAllow, emphasis: 'secondary' }, phone, later],
        error,
      };
}

export function NotifyFallback() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const notify = useNotifyState();
  const { approvals } = useApprovals();
  const ready = useOrgus((s) => s.phase === 'ready');
  const mock = useOrgus((s) => s.mode === 'mock');
  const permission = usePwa((s) => s.permission);
  const supported = permission !== 'unsupported';
  const active = ready && fallbackActive(notify, supported);
  const [store, setStore] = useState(() => readStore(storage()));
  const seen = useRef<ReadonlySet<string> | null>(null);
  const candidates = useMemo(() => candidateAlerts(notify, approvals, formatClock), [notify, approvals]);

  // Raise what arrived since the tab loaded; everything present at load counts as seen. The
  // baseline waits for a source to arrive (the approvals slice can come after the first paint).
  const sourceLoaded = !!notify || !!approvals;
  // Candidates come from the notify module once it reports, else from approvals. When notify
  // reports after approvals did, what it lists is not news: take the baseline again.
  const source = notify ? 'notify' : 'approvals';
  const sourceSeen = useRef(source);
  useEffect(() => {
    if (sourceSeen.current !== source) {
      sourceSeen.current = source;
      seen.current = null;
    }
    const shown = readStore(storage()).shown;
    const r = fallbackStep(seen.current, candidates, { loaded: ready && sourceLoaded, active, shown });
    seen.current = r.seen;
    if (!r.fresh.length) return;
    write(SHOWN_KEY, remember(shown, r.fresh.map((a) => a.key)));
    for (const a of r.fresh) deliver(a);
  }, [candidates, active, ready, sourceLoaded, source]);

  function deliver(a: FallbackAlert): void {
    // A request is answered where the person is (ADR 0072), in the answer sheet; anything else opens its page.
    const approval = approvalOfUrl(a.url);
    const open = () => (approval ? openAnswer(approval) : navigate(a.url));
    const perm = typeof Notification === 'undefined' ? 'denied' : Notification.permission;
    const how = deliveryOf({ visible: document.visibilityState === 'visible', focused: document.hasFocus(), permission: perm });
    if (how === 'os') {
      try {
        const n = new Notification(a.title, { body: a.body, tag: a.tag, requireInteraction: a.requireInteraction, icon: '/icons/icon-192.png', badge: '/icons/badge-96.png' });
        n.onclick = () => {
          window.focus();
          open();
          n.close();
        };
        return;
      } catch {
        /* some browsers only notify from a service worker: fall back to the toast */
      }
    }
    toast(a.title, { tone: 'neutral', description: a.body, action: { label: approval ? 'Answer' : 'Open', onClick: open }, ...(a.requireInteraction ? { duration: 0 } : {}) });
  }

  // The tab title carries the count of waiting and blocked requests, whatever page sets the title.
  const count = attentionCount(approvals);
  useEffect(() => {
    const apply = () => {
      const t = titleWithCount(document.title, count);
      if (t !== document.title) document.title = t;
    };
    apply();
    const el = document.querySelector('title');
    const mo = el ? new MutationObserver(apply) : null;
    mo?.observe(el!, { childList: true, characterData: true, subtree: true });
    return () => {
      mo?.disconnect();
      document.title = titleWithCount(document.title, 0);
    };
  }, [count]);

  usePwa((s) => `${s.endpoint}|${s.standalone}|${!!s.registration}`);
  const push = canPushHere(notify?.vapidPublicKey, mock);
  const state = bannerState({ active, permission, dismissedAt: store.dismissedAt, deniedSeen: store.deniedSeen, now: Date.now(), canPush: push });

  const [error, setError] = useState<unknown>(null);
  const allow = async () => {
    setError(null);
    try {
      alertsOnToast(await turnOnAlerts(notify?.vapidPublicKey, mock));
    } catch (e) {
      setError(toUiError(e, { what: 'Alerts were not turned on.' }));
    }
  };
  const dismiss = () => {
    const next = { dismissedAt: Date.now(), deniedSeen: store.deniedSeen || state === 'denied' };
    write(DISMISS_KEY, next);
    setStore((s) => ({ ...s, ...next }));
  };

  // Settings, Notifications says all of this itself (This device, and the no-channel callout): no notice on top.
  const onSettings = pathname.startsWith('/settings/notifications');
  useNotice(onSettings ? null : fallbackNotice({ state, push, onAllow: allow, onDismiss: dismiss, error }));
  return null;
}
